<template>
  <div>
    <div class="announcement-detail">
      <!--<span style="float: right">-->
      <announcement-status
        :icon-type="record.icon"
        :status="record.status"
      />
      <permission-wrapper
        skip-alert
        page-name="announcements"
        resource-name="announcement"
        action-name="delete">
        <announcement-remove
          v-if="record.id!=='new'"
          :on-click="() => onRemove(record.id)"
        />
      </permission-wrapper>
      <!--</span>-->
    </div>
    <a-divider />
    <announcement-editor
      v-if="isLoaded"
      :disabled="loading || updateDisabled"
      :on-text-changed="onChangeLabel"
      :on-background-color-changed="onBackgroundColorChanged"
      :editable-text="record.label"
      :custom-background-color="record.backgroundColor"
    />
    <a-divider />
    <a-row
      type="flex"
      v-if="isLoaded">
      <a-col :span="2">
        {{ globalize('announcement-detail.lbl-network') }}
      </a-col>
      <a-col :span="18">
        <a-select
          v-if="networks.length > 0"
          style="width: 35%"
          :disabled="loading || updateDisabled"
          :placeholder="`announcement-detail.lbl-network-placeholder` | globalize"
          :default-value="network.id"
          :value="network.id"
          @change="onNetworkChanged"
        >
          <a-select-option
            v-for="row in networks"
            :value="row.id"
            :key="`networks-${row.id}`">
            {{ row.name }}
          </a-select-option>
        </a-select>
      </a-col>
    </a-row>
    <a-row
      type="flex"
      v-if="isLoaded">
      <a-col :span="2">
        {{ globalize('announcement-detail.lbl-dates') }}
      </a-col>

      <a-col :span="12">
        <a-range-picker
          @calendarChange="onCalendarChanged"
          :disabled="loading || updateDisabled"
          :disabled-date="isDateDisabled"
          :disabled-time="disabledTime"
          :default-value="[moment(record.startDate, dateFormat),
                           moment(record.endDate, dateFormat)]"
          :format="dateFormat"
        />
      </a-col>
    </a-row>
    <a-row
      type="flex"
      v-if="isLoaded">
      <a-col :span="2">
        {{ globalize('announcement-detail.lbl-pages') }}
      </a-col>
      <a-col :span="12">
        <span class="app-key-value__value">
          <a-select
            mode="tags"
            :disabled="loading || updateDisabled"
            placeholder="Please select page by click"
            :default-value="record.pages.map(p => p.value)"
            :value="record.pages.map(p => p.value)"
            class="announcement-detail-pages-selector app-key-value__value"
            style="width: 50%"
            allow-clear
            @change="onPagesChanged"
          >
            <a-select-option
              value="all"
              key="pages-select-all">
              {{ 'announcement-detail.select-all-pages' | globalize }}
            </a-select-option>

            <a-select-option
              v-for="page in ANNOUNCEMENT_PAGES"
              :value="page.value"
              :key="`${page.value}`">
              {{ page.label }}
            </a-select-option>
          </a-select>
          <span v-if="record.pages.length === 0">
            {{ 'announcement-detail.all-pages-will-be-used' | globalize }}
          </span>
        </span>
      </a-col>
    </a-row>
    <a-row
      type="flex"
      v-if="isLoaded">
      <a-col :span="2">
        {{ globalize('announcement-detail.lbl-countries') }}
      </a-col>
      <a-col :span="22">
        <span class="app-key-value__value">
          <a-select
            mode="tags"
            :disabled="loading || updateDisabled"
            placeholder="Please select countries by click"
            :default-value="record.countries.map(p => p.code)"
            :value="record.countries.map(p => p.code)"
            class="announcement-detail-countries-selector app-key-value__value"
            allow-clear
            :filter-option="onCountrySearch"
            @change="onCountriesChanged"
          >
            <a-select-option
              v-for="country in countries"
              :value="country.code"
              :key="`${country.name}`">
              <span
                role="img"
                :aria-label="country.code">{{ country.flag }}</span>
              {{ country.name }}
            </a-select-option>
          </a-select>
          <span v-if="record.countries.length === 0">
            {{ 'announcement-detail.all-countries-will-be-used' | globalize }}
          </span>
        </span>
      </a-col>
    </a-row>
    <div class="announcement-details__actions-wrp">
      <permission-wrapper
        skip-alert
        page-name="announcements"
        resource-name="announcement"
        action-name="update">
        <a-button
          v-ripple
          :loading="loading"
          :disabled="!validated"
          @click="saveRecord"
          type="primary"
          class="app__form-submit-btn app__button-raised"
        >
          {{ 'announcement-detail.btn-save' | globalize }}
        </a-button>
        <a-button
          v-ripple
          :loading="loading"
          :disabled="!validated"
          @click="saveAndClose"
          type="primary"
          class="app__form-submit-btn app__button-raised"
        >
          {{ 'announcement-detail.btn-save-and-close' | globalize }}
        </a-button>
      </permission-wrapper>
    </div>
  </div>
</template>

<script>
import { globalize } from '@/vue/filters/globalize'
import moment from 'moment'
import ARow from 'ant-design-vue/lib/row'
import ACol from 'ant-design-vue/lib/col'
import ADatePicker from 'ant-design-vue/lib/date-picker'
import ASelect from 'ant-design-vue/lib/select'
import AButton from 'ant-design-vue/lib/button'
import ADivider from 'ant-design-vue/lib/divider'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import { vueRoutes } from '@/vue-router/routes'
import { ErrorHandler } from '@/js/helpers/error-handler'
import {
  AnnouncementRecord,
  ANNOUNCEMENT_STATUSES,
  ANNOUNCEMENT_STATUSES_AS_OBJECT,
  ANNOUNCEMENT_PAGES } from '@/js/records/entities/announcement.record'
import AnnouncementStatus from './components/AnnouncementStatus'
import AnnouncementEditor from './components/AnnouncementEditor'
import AnnouncementRemove from './components/AnnouncementRemove'
import { countries } from 'country-list-json'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import PermissionMixin from '@/vue/mixins/permission.mixin'

export default {
  name: 'announcement-detail',
  components: {
    AnnouncementEditor,
    AnnouncementStatus,
    AnnouncementRemove,
    ASelect,
    'a-select-option': ASelect.Option,
    'a-range-picker': ADatePicker.RangePicker,
    ARow,
    ACol,
    AButton,
    ADivider,
    PermissionWrapper,
  },
  mixins: [ PermissionMixin ],
  props: {
    id: { type: [String, Number], required: true },
  },
  data () {
    return {
      label: globalize('announcement-detail.add-new'),
      validated: false,
      loading: false,
      isLoaded: false,
      networks: [],
      network: {},
      errors: {},
      record: new AnnouncementRecord({}),
      editor: null,
      ANNOUNCEMENT_STATUSES,
      dateFormat: 'YYYY/MM/DD',
      ANNOUNCEMENT_PAGES,
      moment,
      countries,
    }
  },

  computed: {
    defaultPages () {
      if (this.record && this.record.pages === 0) {
        return []
      }
      const result = this.record.pages.map(p => p.value)
      return result
    },
    defaultCountries: () => {},
    updateDisabled () {
      const result = this.isForbidden('announcements', 'announcement', 'update')
      return result
    },
  },

  async created () {
    this.networks = await this.getNetworks()

    if (this.id === 'new') {
      this.isLoaded = true
    } else {
      const record = await this.getData()

      this.record = new AnnouncementRecord(record)
      this.network = this.networks.find(n => +n.id === this.record.chainId)

      this.isLoaded = true

      this.label = record.label

      const valid = this.record.validate()
      this.validated = valid.valid
    }
  },
  methods: {
    async getNetworks () {
      this.loading = true
      this.isFailed = false
      let response = {}
      try {
        response = await api.getWithSignature('/supported-networks')
        return response.data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    async onAddNetwork (network) {
      this.loading = true
      this.isFailed = false
      let body = {
        data: {
          type: 'add_supported_network',
          attributes: {
            name: network.name,
            network: network.network,
            network_id: parseInt(network.networkId, 10),
            chain_id: parseInt(network.chainId, 10),
            rpc: network.rpc,
            subgraph_url: network.subgraphUrl,
            smt_distributor_contract: network.smtDistributorContract,
          },
        },
      }
      try {
        const bodyAsString = JSON.stringify(body)

        await api.postWithSignature(`/supported-networks`, bodyAsString)

        this.networks = await this.getNetworks()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    async getData () {
      this.loading = true
      this.isFailed = false
      let response = {}
      let query = {
        filter: clearObject({
          id: this.id,
        }),
        page: {
          limit: 1,
        },
      }

      try {
        response = await api.getWithSignature('/announcements/list', query)
        return response.data[0]
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    async saveRecord () {
      this.loading = true
      this.isFailed = false

      const validated = this.record.validate()
      this.validated = validated.valid

      if (!this.validated) {
        return
      }

      let body = {
        data: {
          type: 'announcement',
          attributes: {
            start_date: this.record.startDate,
            end_date: this.record.endDate,
            label: this.record.label,
            chain_id: this.record.chainId,
            background_color: this.record.backgroundColor,
            pages: this.record.pages.map(p => p.value),
            countries: this.record.countries.map(p => p.code),
          },
        },
      }

      try {
        if (this.record.id === 'new') {
          body.data.type = 'add_announcement'
          const bodyAsString = JSON.stringify(body)
          const response = await api.postWithSignature(`/announcements`, bodyAsString)

          this.record = new AnnouncementRecord(response.data)

          this.$router.push({
            name: vueRoutes.announcementDetails.name,
            params: {
              id: response.data.id,
            },
          })
        } else {
          const bodyAsString = JSON.stringify(body)
          await api.putWithSignature(`/announcements/${this.record.id}`, bodyAsString)
        }
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    async saveAndClose () {
      await this.saveRecord()
      this.$router.push({
        name: vueRoutes.announcementList.name,
      })
    },

    async onRemove (id) {
      this.loading = true
      try {
        await api.deleteWithSignature(`/announcements/${id}`)

        this.$router.push({
          name: vueRoutes.announcementList.name,
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.loading = false
      }
    },

    onPagesChanged (value) {
      this.record.updatePages(value)

      const valid = this.record.validate()
      this.validated = valid.valid
    },

    onCountriesChanged (value) {
      this.record.updateCountries(value)

      const valid = this.record.validate()
      this.validated = valid.valid
    },

    onCountrySearch (value, option) {
      const re = new RegExp(`${value}.+`, 'ig')
      const result = re.test(option.key)
      return result
    },

    onCalendarChanged (value) {
      const current = new Date()
      this.record.startDate = value[0].toDate()
      if (value[1]) {
        this.record.endDate = value[1].toDate()
      }
      if (current < this.record.startDate) {
        this.record.status = ANNOUNCEMENT_STATUSES_AS_OBJECT.waiting
      }
      if (current > this.record.endDate) {
        this.record.status = ANNOUNCEMENT_STATUSES_AS_OBJECT.closed
      }
      if (current <= this.record.endDate && current >= this.record.startDate) {
        this.record.status = ANNOUNCEMENT_STATUSES_AS_OBJECT.active
      }
      const valid = this.record.validate()
      this.validated = valid.valid
    },

    range (start, end) {
      const result = []
      for (let i = start; i < end; i++) {
        result.push(i)
      }
      return result
    },

    isDateDisabled (current) {
      // Can not select days before today and today
      return current && current < moment().startOf('day')
    },

    disabledTime (_, type) {
      const current = new Date()
      return {
        disabledHours: () => this.range(0, current.getHours()),
        disabledMinutes: () => this.range(current.getMinutes(), 60),
        disabledSeconds: () => this.range(current.getSeconds(), 60),
      }
    },

    onBackgroundColorChanged (color) {
      this.record.backgroundColor = color
    },

    onNetworkChanged (val) {
      this.record.chainId = +val
      this.network = this.networks.find(n => n.id === val)
    },

    onChangeLabel (label) {
      this.record.label = label

      const valid = this.record.validate()
      this.validated = valid.valid
    },

    globalize,
  },
}

</script>

<style lang="scss">
  @import '~@scss/variables';
  @import '~ant-design-vue/lib/date-picker/style/index.css';
  @import '~ant-design-vue/lib/select/style/index.css';
  @import '~ant-design-vue/lib/grid/style/index.css';
  @import '~ant-design-vue/lib/button/style/index.css';
  @import '~ant-design-vue/lib/divider/style/index.css';

  .announcement-detail {
    min-height: 2em;
  }

  .announcement-detail-editor {
    margin-top: 1.25em;
  }

  .announcement-detail-editor > * {
    padding: 1.5em;
    background: $_white;
    min-height: 6.25em;
  }

  .announcement-details__actions-wrp {
    display: flex;
    gap: 1rem;
  }

  .ant-select-selection__choice {
    margin-right: 0.5em;
  }

  .ant-select-selection__choice__content {
    padding-right: 1em;
  }

  .announcement-detail-label {
    width: 50%;
  }

  .mdi-plus.increased {
    font-size: 1em;
  }

  .announcement-detail-countries-selector {
    width: 80%;
  }
</style>
